import { useEffect, useMemo, useState, type FormEvent } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminInput,
  AdminPagination,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
} from './AdminUi'
import '../styles/admin-customers.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type CustomerTypeFilter = 'all' | 'first-time' | 'returning'
type PendingFilter = 'all' | 'has-pending' | 'no-pending'
type CustomerSort = 'latest' | 'paid-spend' | 'orders'

type CustomerSummary = {
  customerKey: string
  name: string
  email: string
  phone: string
  latestSuburb: string
  orderCount: number
  paidSpendCents: number
  pendingOrderCount: number
  firstOrderAt: string
  lastOrderAt: string
  latestOrderId: string | null
}

type CustomersPayload = {
  identityRule: 'normalized-email'
  source: 'orders.order_payload_json'
  page: number
  pageSize: number
  total: number
  totalPages: number
  metrics: {
    totalCustomers: number
    newThisWeek: number
    returningCustomers: number
    averagePaidOrderValueCents: number
    paidOrderCount: number
    totalPaidRevenueCents: number
    incompleteOrderCount: number
  }
  customers: CustomerSummary[]
}

type LinkedOrder = {
  orderId: string
  paymentStatus: 'paid' | 'pending'
  totalCents: number
  createdAt: string
}

type CustomerDetail = {
  customerKey: string
  identityRule: 'normalized-email'
  name: string
  email: string
  phone: string
  latestDelivery: { address: string; suburb: string; postcode: string }
  orderCount: number
  paidSpendCents: number
  pendingOrderCount: number
  firstOrderAt: string
  lastOrderAt: string
  orders: LinkedOrder[]
}

const NZD = new Intl.NumberFormat('en-NZ', {
  style: 'currency',
  currency: 'NZD',
  minimumFractionDigits: 2,
})
const DATE_ONLY = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function parseStoredTimestamp(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return new Date(Number.NaN)
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) return new Date(trimmed)
  return new Date(`${trimmed.replace(' ', 'T')}Z`)
}

function formatMoney(cents: number) {
  return NZD.format(cents / 100)
}

function formatDate(value: string) {
  const date = parseStoredTimestamp(value)
  return Number.isNaN(date.getTime()) ? '—' : DATE_ONLY.format(date)
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function PaymentPill({ status }: { status: LinkedOrder['paymentStatus'] }) {
  return (
    <AdminStatusPill tone={status === 'paid' ? 'success' : 'warning'}>
      {status === 'paid' ? 'Paid' : 'Awaiting payment'}
    </AdminStatusPill>
  )
}

function initialCustomerSearch() {
  return (new URLSearchParams(window.location.search).get('search') ?? '').trim().slice(0, 80)
}

export default function AdminCustomersPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [data, setData] = useState<CustomersPayload | null>(null)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState(() => initialCustomerSearch())
  const [search, setSearch] = useState(() => initialCustomerSearch())
  const [customerType, setCustomerType] = useState<CustomerTypeFilter>('all')
  const [pendingFilter, setPendingFilter] = useState<PendingFilter>('all')
  const [sort, setSort] = useState<CustomerSort>('latest')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetail | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [detailBusy, setDetailBusy] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  async function loadCustomerDetail(customerKey: string) {
    setSelectedKey(customerKey)
    setDetailBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/customers/${customerKey}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/customers')
        return
      }
      if (!response.ok) throw new Error('customer unavailable')
      const nextCustomer = (await response.json() as { customer: CustomerDetail }).customer
      setSelectedCustomer(nextCustomer)
    } catch {
      setSelectedCustomer(null)
      setError('That customer could not be loaded. Please try again.')
    } finally {
      setDetailBusy(false)
    }
  }

  async function loadCustomers() {
    setBusy(true)
    setError('')
    try {
      const params = new URLSearchParams({
        page: String(page),
        type: customerType,
        pending: pendingFilter,
        sort,
      })
      if (search) params.set('search', search)

      const [sessionResponse, customersResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin' }),
        fetch(`/api/admin/customers?${params.toString()}`, { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || customersResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/customers')
        return
      }
      if (!sessionResponse.ok || !customersResponse.ok) throw new Error('customers unavailable')

      const nextSession = await sessionResponse.json() as SessionPayload
      const nextData = await customersResponse.json() as CustomersPayload
      setSession(nextSession)
      setData(nextData)
      if (nextData.page !== page) setPage(nextData.page)

      const selectedStillVisible = selectedKey
        ? nextData.customers.some((customer) => customer.customerKey === selectedKey)
        : false
      const nextSelectedKey = selectedStillVisible ? selectedKey : nextData.customers[0]?.customerKey ?? null

      if (!nextSelectedKey) {
        setSelectedKey(null)
        setSelectedCustomer(null)
      } else if (nextSelectedKey !== selectedKey || selectedCustomer?.customerKey !== nextSelectedKey) {
        await loadCustomerDetail(nextSelectedKey)
      }
    } catch {
      setError('Customers could not be loaded. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void loadCustomers()
    // The load is intentionally keyed only to the applied directory controls below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, customerType, pendingFilter, sort])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  function clearDirectoryControls() {
    setSearchInput('')
    setSearch('')
    setCustomerType('all')
    setPendingFilter('all')
    setSort('latest')
    setPage(1)
  }

  const hasAppliedControls = Boolean(search) || customerType !== 'all' || pendingFilter !== 'all' || sort !== 'latest'

  if (error && (!session || !data)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Customers unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => void loadCustomers()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session || !data) {
    return (
      <main className="admin-shell-gate-loading" aria-label="Loading Customers">
        <AdminCard><AdminSkeleton lines={8} /></AdminCard>
      </main>
    )
  }

  return (
    <AdminShell
      title="Customers"
      eyebrow="OPERATIONS"
      description="View customer activity and order history."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={(
        <AdminButton variant="secondary" type="button" disabled={busy} onClick={() => void loadCustomers()}>
          Refresh customers
        </AdminButton>
      )}
    >
      <div className="admin-customers-page">
        {error && <div className="admin-customers__inline-error" role="alert">{error}</div>}

        <section className="admin-customers__summary" aria-label="Customer summary">
          <article className="admin-customers__summary-card">
            <span>Total customers</span>
            <strong>{data.metrics.totalCustomers}</strong>
            <small>Unique customers from orders</small>
          </article>
          <article className="admin-customers__summary-card">
            <span>New this week</span>
            <strong>{data.metrics.newThisWeek}</strong>
            <small>Customers placing their first order this week</small>
          </article>
          <article className="admin-customers__summary-card">
            <span>Returning customers</span>
            <strong>{data.metrics.returningCustomers}</strong>
            <small>Customers with more than one order</small>
          </article>
          <article className="admin-customers__summary-card">
            <span>Average paid order value</span>
            <strong>{formatMoney(data.metrics.averagePaidOrderValueCents)}</strong>
            <small>Paid orders only</small>
          </article>
        </section>

        <section className="admin-customers__controls" aria-label="Customer directory controls">
          <form className="admin-customers__search-form" onSubmit={submitSearch}>
            <label className="admin-customers__search-field">
              <span className="admin-sr-only">Search customers</span>
              <AdminInput
                type="search"
                value={searchInput}
                maxLength={80}
                placeholder="Search name, email, phone or SG order number"
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>
            <AdminButton type="submit">Search</AdminButton>
          </form>

          <label className="admin-customers__select-field">
            <span>Customer type</span>
            <AdminSelect value={customerType} onChange={(event) => { setPage(1); setCustomerType(event.target.value as CustomerTypeFilter) }}>
              <option value="all">All customers</option>
              <option value="first-time">First-time customers</option>
              <option value="returning">Returning customers</option>
            </AdminSelect>
          </label>

          <label className="admin-customers__select-field">
            <span>Payment</span>
            <AdminSelect value={pendingFilter} onChange={(event) => { setPage(1); setPendingFilter(event.target.value as PendingFilter) }}>
              <option value="all">All payment states</option>
              <option value="has-pending">Has awaiting payment</option>
              <option value="no-pending">No awaiting payment</option>
            </AdminSelect>
          </label>

          <label className="admin-customers__select-field">
            <span>Sort</span>
            <AdminSelect value={sort} onChange={(event) => { setPage(1); setSort(event.target.value as CustomerSort) }}>
              <option value="latest">Latest order</option>
              <option value="paid-spend">Highest paid spend</option>
              <option value="orders">Most orders</option>
            </AdminSelect>
          </label>

          {hasAppliedControls && (
            <AdminButton variant="ghost" type="button" onClick={clearDirectoryControls}>Clear</AdminButton>
          )}
        </section>

        <div className="admin-customers__workspace-grid">
          <AdminCard padding="none" className="admin-customers__directory-card">
            <header className="admin-customers__directory-header">
              <div>
                <h2>Customer directory</h2>
                <p>{data.total} {data.total === 1 ? 'customer' : 'customers'} {hasAppliedControls ? 'matching the current view' : 'in the directory'}</p>
              </div>
              {data.metrics.incompleteOrderCount > 0 && (
                <span className="admin-customers__data-note">
                  {data.metrics.incompleteOrderCount} incomplete {data.metrics.incompleteOrderCount === 1 ? 'order is' : 'orders are'} excluded from customer grouping
                </span>
              )}
            </header>

            {data.customers.length > 0 ? (
              <>
                <div className="admin-customers__table-wrap">
                  <table className="admin-customers__table">
                    <caption className="admin-sr-only">Sugar Gremlin derived customer directory</caption>
                    <thead>
                      <tr>
                        <th scope="col">Customer</th>
                        <th scope="col">Contact</th>
                        <th scope="col">Orders</th>
                        <th scope="col">Paid lifetime spend</th>
                        <th scope="col">First order</th>
                        <th scope="col">Latest order</th>
                        <th scope="col">Payment</th>
                        <th scope="col"><span className="admin-sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.customers.map((customer) => {
                        const selected = customer.customerKey === selectedKey
                        return (
                          <tr key={customer.customerKey} className={selected ? 'is-selected' : undefined} aria-selected={selected || undefined}>
                            <td data-label="Customer">
                              <div className="admin-customers__cell-stack">
                                <strong>{customer.name}</strong>
                                <span>{customer.latestSuburb || 'Suburb not recorded'}</span>
                              </div>
                            </td>
                            <td data-label="Contact">
                              <div className="admin-customers__cell-stack">
                                <strong>{customer.email}</strong>
                                <span>{customer.phone || 'Phone not recorded'}</span>
                              </div>
                            </td>
                            <td data-label="Orders"><strong>{customer.orderCount}</strong></td>
                            <td data-label="Paid lifetime spend"><strong className="admin-customers__money">{formatMoney(customer.paidSpendCents)}</strong></td>
                            <td data-label="First order"><span className="admin-customers__date">{formatDate(customer.firstOrderAt)}</span></td>
                            <td data-label="Latest order">
                              <div className="admin-customers__cell-stack">
                                <strong>{formatDate(customer.lastOrderAt)}</strong>
                                <span>{customer.latestOrderId || 'Order ID unavailable'}</span>
                              </div>
                            </td>
                            <td data-label="Payment">
                              {customer.pendingOrderCount > 0
                                ? <AdminStatusPill tone="warning">{customer.pendingOrderCount} awaiting</AdminStatusPill>
                                : <span className="admin-customers__muted">No pending</span>}
                            </td>
                            <td data-label="">
                              <AdminButton
                                compact
                                variant={selected ? 'primary' : 'secondary'}
                                type="button"
                                disabled={detailBusy && !selected}
                                aria-pressed={selected}
                                onClick={() => void loadCustomerDetail(customer.customerKey)}
                              >
                                {selected ? 'Selected' : 'View'}
                              </AdminButton>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <footer className="admin-customers__pagination-wrap">
                  <span>Page {data.page} of {data.totalPages}</span>
                  <AdminPagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />
                </footer>
              </>
            ) : (
              <AdminStatePanel
                state="empty"
                title={hasAppliedControls ? 'No matching customers' : 'No customers yet'}
                description={hasAppliedControls ? 'Try clearing or changing the current search and filters.' : 'Customers appear here after authoritative orders contain a usable contact email.'}
                action={hasAppliedControls ? <AdminButton type="button" onClick={clearDirectoryControls}>Clear filters</AdminButton> : undefined}
              />
            )}
          </AdminCard>

          <aside className="admin-customers__detail-panel" aria-label="Selected customer details" aria-busy={detailBusy}>
            {detailBusy && !selectedCustomer ? (
              <AdminCard className="admin-customers__detail-loading"><AdminSkeleton lines={10} /></AdminCard>
            ) : selectedCustomer ? (
              <AdminCard padding="none" className="admin-customers__detail-card">
                <header className="admin-customers__detail-header">
                  <div>
                    <span>Customer details</span>
                    <h2>{selectedCustomer.name}</h2>
                    <p>{selectedCustomer.email}</p>
                  </div>
                  <button
                    className="admin-customers__detail-close"
                    type="button"
                    aria-label="Clear selected customer"
                    onClick={() => { setSelectedKey(null); setSelectedCustomer(null) }}
                  >×</button>
                </header>

                <section className="admin-customers__detail-metrics" aria-label="Selected customer metrics">
                  <div><span>Total orders</span><strong>{selectedCustomer.orderCount}</strong></div>
                  <div><span>Paid lifetime spend</span><strong>{formatMoney(selectedCustomer.paidSpendCents)}</strong></div>
                </section>

                <section className="admin-customers__detail-section">
                  <h3>Current details</h3>
                  <dl className="admin-customers__detail-list">
                    <div><dt>Email</dt><dd>{selectedCustomer.email}</dd></div>
                    <div><dt>Phone</dt><dd>{selectedCustomer.phone || 'Not recorded'}</dd></div>
                    <div><dt>Latest suburb</dt><dd>{selectedCustomer.latestDelivery.suburb || 'Not recorded'}</dd></div>
                    <div><dt>First order</dt><dd>{formatDate(selectedCustomer.firstOrderAt)}</dd></div>
                    <div><dt>Latest order</dt><dd>{formatDate(selectedCustomer.lastOrderAt)}</dd></div>
                    <div>
                      <dt>Awaiting payment</dt>
                      <dd>{selectedCustomer.pendingOrderCount > 0 ? `${selectedCustomer.pendingOrderCount} ${selectedCustomer.pendingOrderCount === 1 ? 'order awaiting' : 'orders awaiting'}` : 'No pending payments'}</dd>
                    </div>
                  </dl>
                  <p className="admin-customers__snapshot-note">Details reflect the customer’s latest order.</p>
                </section>

                <section className="admin-customers__detail-section admin-customers__recent-orders">
                  <div className="admin-customers__section-heading">
                    <h3>Recent orders</h3>
                    <a href="/admin/orders">View orders</a>
                  </div>
                  <div className="admin-customers__recent-list">
                    {selectedCustomer.orders.slice(0, 5).map((order) => (
                      <article key={`${order.orderId}-${order.createdAt}`}>
                        <div>
                          <strong>{order.orderId}</strong>
                          <span>{formatDate(order.createdAt)}</span>
                        </div>
                        <div className="admin-customers__recent-order-meta">
                          <strong>{formatMoney(order.totalCents)}</strong>
                          <PaymentPill status={order.paymentStatus} />
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </AdminCard>
            ) : (
              <AdminCard className="admin-customers__detail-empty">
                <h2>Select a customer</h2>
                <p>Choose a customer from the directory to view current details and recent linked orders.</p>
              </AdminCard>
            )}
          </aside>
        </div>
      </div>
    </AdminShell>
  )
}
